import { DEFAULT_METRICS_CONFIG, MetricsConfig } from 'src/services/openApiV2/utils/api.config'

// Types based on fe-metrics controller - must match exactly
interface MetricData {
  type: string
  name: string
  help?: string
  value: number // Required and must be a number
  labels?: Record<string, string> // Optional string key-value pairs
  buckets?: number[] // Optional array of numbers
}

interface CategorizedMetricData {
  category: string // Required
  metric: string // Required
  value?: number // Optional but must be a number if provided
  duration?: number // Optional but must be a number if provided
  timestamp?: string // Optional
  tags?: Record<string, string> // Optional string key-value pairs

  // Enhanced format support - optional context objects
  user?: {
    id?: string
    type?: string
    email?: string
    [key: string]: any
  }

  page?: {
    url?: string
    title?: string
    referrer?: string
    [key: string]: any
  }
  metadata?: Record<string, any>

  // Optional: explicitly specify metric type
  type?: 'counter' | 'gauge' | 'histogram'

  // Allow additional fields for extensibility
  [key: string]: any
}

type QueuedMetric = {
  endpoint: 'collect' // Only use collect endpoint
  data: CategorizedMetricData // Only categorized metrics
}

class MetricsService {
  private static readonly METRICS_URL = import.meta.env.VITE_METRICS_URL || 'https://metrics.tryloop.ai'
  private static readonly SLACK_URL = 'https://metrics.tryloop.ai/slack/send'
  private static config: MetricsConfig = { ...DEFAULT_METRICS_CONFIG }
  private static instance: MetricsService

  private metricsQueue: QueuedMetric[] = []
  private flushTimeoutId: NodeJS.Timeout | null = null

  // Dynamic queue management
  private queueHighWaterMark = 0
  private readonly minQueueSize = 100
  private readonly maxQueueSize = 10000
  private dynamicQueueSize = 500 // Start with reasonable default

  // Queue statistics for adaptive sizing
  private queueStats = {
    totalEnqueued: 0,
    totalDropped: 0, // Kept for backward compatibility but always 0
    lastQueueSizeUpdate: Date.now(),
    recentDropRate: 0 // Always 0 since we don't drop metrics
  }

  // Slack integration settings
  private slackQueue: QueuedMetric[] = []

  private readonly taskPriority = {
    flush: { timeout: 1000 } as IdleRequestOptions,
    track: { timeout: 2000 } as IdleRequestOptions
  }

  private readonly debounceTimers: Record<string, NodeJS.Timeout> = {}
  private readonly debounceDelay = 1000

  private readonly sampleRates: Record<string, number> = {
    user_behavior: 0.1,
    performance: 1.0,
    error: 1.0,
    default: 1.0
  }

  private readonly timeout = 3000
  private readonly maxRetries = 2
  private readonly baseRetryDelay = 1000
  private readonly maxRetryDelay = 10000

  private readonly networkConfig = {
    slow: { batchSize: 150, flushInterval: 45000 },
    medium: { batchSize: 100, flushInterval: 30000 },
    fast: { batchSize: 50, flushInterval: 15000 }
  }

  private = this.networkConfig.medium

  private constructor() {
    try {
      this.startPeriodicFlush()
    } catch (error) {
      console.error('Failed to initialize metrics service:', error)
      // Service continues to work even if initialization fails
    }
  }

  public static getInstance(): MetricsService {
    try {
      if (!MetricsService.instance) {
        MetricsService.instance = new MetricsService()
      }
      return MetricsService.instance
    } catch (error) {
      console.error('Failed to get metrics service instance:', error)
      // Return a stub instance that does nothing
      return {
        collectMetric: async () => {},
        collectDebounced: async () => {},
        collectCritical: async () => {},
        trackUserLogin: async () => {},
        trackUserSession: async () => {},
        trackUserAction: async () => {},
        trackMemoryUsage: async () => {},
        trackHttpLatency: async () => {},

        trackError: async () => {},
        trackApiError: async () => {},
        trackHealthStatus: async () => {},
        trackScrollDepth: async () => {},

        trackUserBehavior: async () => {},
        trackPerformanceMetric: async () => {},

        pushAPICallsCount: async () => {},
        trackUserLoginWithMagicLink: async () => {},
        trackUserLoginWithPassword: async () => {},
        trackActiveUsers: async () => {},
        cleanup: async () => {},
        sanitizeJSON: () => ({})
      } as any
    }
  }

  public static getConfig(): MetricsConfig {
    return { ...MetricsService.config }
  }

  public static updateConfig(config: Partial<MetricsConfig>): void {
    MetricsService.config = {
      ...MetricsService.config,
      ...config
    }
  }

  public static isEnabled(): boolean {
    return MetricsService.config.enabled
  }

  public static enable(): void {
    MetricsService.config.enabled = true
  }

  public static disable(): void {
    MetricsService.config.enabled = false
  }

  public static enableSlackMetrics(): void {
    MetricsService.config.enableSlackMetrics = true
  }

  public static disableSlackMetrics(): void {
    MetricsService.config.enableSlackMetrics = false
  }

  public sanitizeJSON(input: Record<string, any> | string, type: 'header' | 'body' | 'query'): Record<string, any> | string {
    if (type === 'header' || type === 'query') {
      // TODO: Remove sensitive information
      return input
    }

    if (typeof input === 'string') {
      return input
    }

    return typeof input === 'object' && input !== null ? input : {}
  }

  private async fetchWithTimeout(url: string, options: RequestInit): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      })
      return response
    } finally {
      clearTimeout(timeoutId)
    }
  }

  private async retryOperation(operation: () => Promise<void>): Promise<void> {
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        await operation()
        return
      } catch (error) {
        console.error(`Metrics tracking failed (attempt ${attempt}/${this.maxRetries}):`, error)

        if (attempt === this.maxRetries) {
          console.warn('Max retries reached, dropping metric')
          return
        }

        const delay = Math.min(this.maxRetryDelay, this.baseRetryDelay * Math.pow(2, attempt - 1) * (0.5 + Math.random() * 0.5))
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }

  private shouldSampleEvent(category: string): boolean {
    const sampleRate = this.sampleRates[category] ?? this.sampleRates.default
    return Math.random() < sampleRate
  }

  private startPeriodicFlush() {
    // Use configured flush interval
    const flushInterval = MetricsService.config.flushInterval

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const scheduleNextFlush = () => {
        window.requestIdleCallback(() => {
          Promise.all([
            this.flush().catch((error) => console.error('Failed to flush metrics:', error)),
            this.flushSlackQueue().catch((error) => console.warn('Failed to flush Slack queue:', error))
          ]).finally(() => {
            this.flushTimeoutId = setTimeout(scheduleNextFlush, flushInterval)
          })
        }, this.taskPriority.flush)
      }

      this.flushTimeoutId = setTimeout(scheduleNextFlush, flushInterval)
    } else {
      this.flushTimeoutId = setInterval(() => {
        Promise.all([
          this.flush().catch((error) => console.error('Failed to flush metrics:', error)),
          this.flushSlackQueue().catch((error) => console.warn('Failed to flush Slack queue:', error))
        ])
      }, flushInterval)
    }
  }

  private async flush(): Promise<void> {
    if (this.metricsQueue.length === 0) return

    // Use configured batch sizes
    const baseBatchSize = MetricsService.config.batchSize
    const maxBatchSize = MetricsService.config.maxBatchSize

    // Adaptive batch size based on queue pressure
    const queuePressure = this.metricsQueue.length / this.dynamicQueueSize
    let adaptiveBatchSize = baseBatchSize

    if (queuePressure > 0.8) {
      // Queue is getting full, increase batch size
      adaptiveBatchSize = Math.min(maxBatchSize, baseBatchSize * 2)
    } else if (queuePressure > 0.6) {
      adaptiveBatchSize = Math.min(maxBatchSize, Math.floor(baseBatchSize * 1.5))
    }

    // Process in parallel for better performance when queue is under pressure
    const promises: Promise<void>[] = []

    while (this.metricsQueue.length > 0 && promises.length < 3) {
      const batch = this.metricsQueue.splice(0, adaptiveBatchSize)

      // Send batch using new batch endpoint logic
      if (queuePressure > 0.5) {
        promises.push(this.sendMetricsBatch(batch).catch((err) => console.error('Batch send failed:', err)))
      } else {
        // Sequential batch sending for normal conditions
        await this.sendMetricsBatch(batch)
      }
    }

    // Wait for all parallel sends to complete
    if (promises.length > 0) {
      await Promise.all(promises)
    }
  }

  private async sendMetricsBatch(batch: QueuedMetric[]) {
    if (!MetricsService.isEnabled() || batch.length === 0) return

    // All metrics now go through collect endpoint
    const collectMetrics = batch.map((m) => m.data)

    // Send all metrics to collect endpoint
    await this.sendBatchToEndpoint('/metrics/collect', collectMetrics)

    // Also send to Slack if enabled
    if (MetricsService.config.enableSlackMetrics) {
      this.slackQueue.push(...batch)
      // Batch Slack notifications
      if (this.slackQueue.length >= MetricsService.config.batchSize) {
        this.flushSlackQueue().catch((err) => console.warn('Failed to send metrics to Slack:', err))
      }
    }
  }

  private async sendBatchToEndpoint(endpoint: string, data: (MetricData | CategorizedMetricData)[]) {
    await this.retryOperation(async () => {
      try {
        // Always send as array, even for single metrics
        const response = await this.fetchWithTimeout(`${MetricsService.METRICS_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        })

        if (!response.ok) {
          if (response.status >= 500) {
            throw new Error(`Server error: ${response.status}`)
          } else {
            console.warn(`Dropping metric batch due to ${response.status} response`)
            return
          }
        }
      } catch (error) {
        throw error
      }
    })
  }

  private addToQueue(data: CategorizedMetricData) {
    // Update statistics
    this.queueStats.totalEnqueued++

    // Always add the metric to the queue - never drop
    this.metricsQueue.push({ endpoint: 'collect', data })

    // Update high water mark
    if (this.metricsQueue.length > this.queueHighWaterMark) {
      this.queueHighWaterMark = this.metricsQueue.length
    }

    // Check if we need to adjust queue size
    this.adjustQueueSizeIfNeeded()

    // Handle queue overflow by flushing, not dropping
    if (this.metricsQueue.length >= this.dynamicQueueSize) {
      this.handleQueueOverflow()
    }
  }

  private adjustQueueSizeIfNeeded() {
    const now = Date.now()
    const timeSinceLastUpdate = now - this.queueStats.lastQueueSizeUpdate

    // Adjust every 30 seconds
    if (timeSinceLastUpdate > 30000) {
      // If queue frequently hits the limit, increase size
      if (this.queueHighWaterMark >= this.dynamicQueueSize * 0.9) {
        this.dynamicQueueSize = Math.min(this.maxQueueSize, Math.floor(this.dynamicQueueSize * 1.5))
        console.info(`Increased queue size to ${this.dynamicQueueSize} due to high utilization`)
      }
      // If queue is consistently underutilized, decrease size to save memory
      else if (this.queueHighWaterMark < this.dynamicQueueSize * 0.3) {
        this.dynamicQueueSize = Math.max(this.minQueueSize, Math.floor(this.dynamicQueueSize * 0.8))
        console.info(`Decreased queue size to ${this.dynamicQueueSize} due to low utilization`)
      }

      // Reset statistics
      this.queueStats.lastQueueSizeUpdate = now
      this.queueStats.recentDropRate = 0 // No drops anymore
      this.queueHighWaterMark = this.metricsQueue.length
    }
  }

  private handleQueueOverflow() {
    // When queue is full, trigger an immediate flush instead of dropping metrics

    // Try to flush immediately
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(
        () => {
          this.flush().catch((err) => console.error('Emergency flush failed:', err))
        },
        { timeout: 100 }
      )
    } else {
      // If requestIdleCallback not available, flush immediately
      this.flush().catch((err) => console.error('Emergency flush failed:', err))
    }

    // Increase dynamic queue size to prevent future overflows
    if (this.dynamicQueueSize < this.maxQueueSize) {
      const newSize = Math.min(this.maxQueueSize, Math.floor(this.dynamicQueueSize * 1.5))
      console.info(`Increasing queue size from ${this.dynamicQueueSize} to ${newSize} to handle load`)
      this.dynamicQueueSize = newSize
    }
  }

  private async flushSlackQueue() {
    if (this.slackQueue.length === 0 || !MetricsService.config.enableSlackMetrics) return

    // No rate limiting - send immediately

    // Use configured batch size for Slack
    const batch = this.slackQueue.splice(0, MetricsService.config.batchSize)
    const appVersion = import.meta.env.VITE_APP_VERSION || 'unknown'

    try {
      // Format metrics data for Slack - all metrics are now categorized
      const metricsData = batch.map((metric) => {
        const data = metric.data
        return {
          type: 'categorized',
          category: data.category,
          metric: data.metric,
          value: data.value,
          tags: data.tags,
          timestamp: data.timestamp
        }
      })

      const payload = {
        message: `\`\`\`${JSON.stringify(metricsData, null, 2)}\`\`\``,
        title: `Frontend Metrics (${batch.length} events) - ${appVersion}`,
        channel: MetricsService.getConfig().slackChannel,
        footer: {
          sent_at: new Date().toISOString(),
          batch_size: batch.length,
          app_version: appVersion
        }
      }

      // Send to Slack without retry
      try {
        const response = await this.fetchWithTimeout(MetricsService.SLACK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          const errorText = await response.text()
          console.error(`Slack API error (${response.status}):`, errorText)
          return
        }

        // Success - parse and log response
        const result = await response.json()
      } catch (error) {
        console.error('Failed to send metrics to Slack:', error)
      }

      // Put the batch back in queue if it's critical data
      // Uncomment if you want to retry these metrics later
      // this.slackQueue.unshift(...batch.map(data => ({ endpoint: data.endpoint, data: data.data })))
    } catch (error) {
      // Unexpected error in the whole process
      console.error('Unexpected error in Slack queue flush:', error)
    }
  }

  // Public API for collecting categorized metrics with enhanced context support
  public async collectMetric(category: string, metric: string, value?: number, tags?: Record<string, string>, type?: 'counter' | 'gauge' | 'histogram') {
    const enhancedContext: {
      user?: { id?: string; type?: string; email?: string; [key: string]: any }
      page?: { url?: string; title?: string; referrer?: string; [key: string]: any }
      metadata?: Record<string, any>
    } = {}

    try {
      if (!MetricsService.isEnabled()) return

      if (!this.shouldSampleEvent(category)) {
        return
      }

      // Merge tags with network_type
      const allTags = {
        ...tags
      }

      // Filter tags based on allowed tags for this metric

      const data: CategorizedMetricData = {
        category,
        metric,
        value,
        timestamp: new Date().toISOString(),
        tags: allTags,
        ...enhancedContext,
        type
      }

      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          try {
            this.addToQueue(data)
          } catch (err) {
            console.warn('Failed to queue collect metric:', err)
          }
        }, this.taskPriority.track)
      } else {
        setTimeout(() => {
          try {
            this.addToQueue(data)
          } catch (err) {
            console.warn('Failed to queue collect metric:', err)
          }
        }, 0)
      }
    } catch (error) {
      // Silently fail - metrics should never break the app
      console.warn('Failed to collect metric:', error)
    }
  }

  public async collectDebounced(
    category: string,
    metric: string,
    value?: number,
    tags?: Record<string, string>,
    enhancedContext?: {
      user?: { id?: string; type?: string; email?: string; [key: string]: any }
      session?: { id?: string; duration?: number; [key: string]: any }
      page?: { url?: string; title?: string; referrer?: string; [key: string]: any }
      metadata?: Record<string, any>
      type?: 'counter' | 'gauge' | 'histogram'
    }
  ) {
    const key = `${category}-${metric}-${JSON.stringify(tags)}`

    if (this.debounceTimers[key]) {
      clearTimeout(this.debounceTimers[key])
    }

    this.debounceTimers[key] = setTimeout(() => {
      this.collectMetric(category, metric, value, tags)
      delete this.debounceTimers[key]
    }, this.debounceDelay)
  }

  public async collectCritical(
    category: string,
    metric: string,
    value?: number,
    tags?: Record<string, string>,
    enhancedContext?: {
      user?: { id?: string; type?: string; email?: string; [key: string]: any }
      session?: { id?: string; duration?: number; [key: string]: any }
      page?: { url?: string; title?: string; referrer?: string; [key: string]: any }
      metadata?: Record<string, any>
      type?: 'counter' | 'gauge' | 'histogram'
    }
  ) {
    await this.collectMetric(category, metric, value, tags)
    if (this.metricsQueue.length >= Math.min(10, this.networkConfig.medium.batchSize)) {
      await this.flush()
    }
  }

  // Convenience methods for common metrics

  // User Metrics - Enhanced for time range tracking
  public async trackUserLogin(
    loginType?: string,
    userContext?: {
      userId?: string
      userType?: string
      userCategory?: 'internal' | 'external' // Loop Kitchen staff vs restaurant staff/customers
      orgId?: string
      orgName?: string
      email?: string
      deviceType?: string
      success?: boolean
    }
  ) {
    const baseLabels = {
      login_type: loginType || 'unknown',
      user_type: userContext?.userType || 'unknown',
      user_category: userContext?.userCategory || 'external', // Default to external
      org_id: userContext?.orgId || 'unknown',
      device_type: userContext?.deviceType || 'unknown',
      // Time-based labels for range queries
      date: new Date().toISOString().split('T')[0],
      hour: new Date().getHours().toString(),
      success: (userContext?.success !== false).toString()
    }

    const enhancedContext = {
      user: {
        id: userContext?.userId || 'anonymous',
        type: userContext?.userType || 'unknown',
        category: userContext?.userCategory || 'external',
        email: userContext?.email,
        org_id: userContext?.orgId,
        org_name: userContext?.orgName
      },
      metadata: {
        event_type: 'user_login',
        timestamp: new Date().toISOString()
      },
      type: 'counter' as const
    }

    if (userContext?.success !== false) {
      // Track successful login
      await this.collectMetric('user', 'login_success', 1, baseLabels, 'counter' as const)

      // Track daily active users for time range analysis
      await this.collectMetric(
        'user',
        'daily_active',
        1,
        {
          date: baseLabels.date,
          user_type: baseLabels.user_type,
          user_category: baseLabels.user_category,
          org_id: baseLabels.org_id
        },
        'gauge' as const
      )
    } else {
      // Track failed login
      await this.collectMetric(
        'user',
        'login_failure',
        1,
        {
          ...baseLabels,
          failure_reason: 'authentication_failed'
        },
        'counter' as const
      )
    }
  }

  public async trackUserSession(userType: string) {
    await this.collectMetric('user', 'session', 1, { user_type: userType })
  }

  public async trackUserAction(actionType: string, userType: string) {
    await this.collectMetric('user', 'action', 1, { action_type: actionType, user_type: userType })
  }

  // Performance Metrics
  public async trackMemoryUsage(bytes: number) {
    await this.collectMetric('performance', 'memory', bytes)
  }

  public async trackCPUUsage(percentage: number) {
    await this.collectMetric('performance', 'cpu', percentage)
  }

  // Latency Metrics - Updated to include user_id, page_path, and module
  public async trackHttpLatency(
    {
      method,
      apiPath,
      statusCode,
      routePath,
      module
    }: {
      method: string
      apiPath: string
      statusCode: number
      routePath?: string
      module?: string
    },
    duration?: number
  ) {
    // Check if API latency tracking is enabled
    if (!MetricsService.config.enabled || !MetricsService.config.enableApiLatencyTracking) {
      return
    }
    try {
      // Clean API path to remove timestamps from query params
      let cleanedApiPath = apiPath
      if (apiPath.includes('?')) {
        try {
          const url = new URL(apiPath, window.location.origin)
          // Remove common timestamp parameters
          url.searchParams.delete('ts')
          url.searchParams.delete('timestamp')
          url.searchParams.delete('_t')
          url.searchParams.delete('t')
          cleanedApiPath = url.pathname + (url.search ? url.search : '')
        } catch {
          // If URL parsing fails, include the path only
          cleanedApiPath = apiPath.split('?')[0]
        }
      }

      const tags: Record<string, string> = {
        method,
        api_path: cleanedApiPath,
        status_code: statusCode.toString(),
        module: module || 'unknown'
      }

      // Add route_path - use provided value or current location
      // Clean URL to remove timestamps from query params
      const currentRoutePath = routePath || window.location.pathname
      if (currentRoutePath.includes('?')) {
        try {
          const url = new URL(currentRoutePath, window.location.origin)
          // Remove common timestamp parameters
          url.searchParams.delete('ts')
          url.searchParams.delete('timestamp')
          url.searchParams.delete('_t')
          url.searchParams.delete('t')
          tags.route_path = url.pathname + (url.search ? url.search : '')
        } catch {
          // If URL parsing fails, include the path only
          tags.route_path = currentRoutePath.split('?')[0]
        }
      } else {
        tags.route_path = currentRoutePath
      }
      await this.collectMetric('latency', 'http', duration, tags, 'histogram')
    } catch (error) {
      // Silently fail - metrics should never break the app
      console.warn('Failed to track HTTP latency:', error)
    }
  }

  // Error Metrics with extended information - Enhanced for business requirements
  public async trackError(
    errorType: string = 'unknown',
    errorCode: string | number = 'unknown',
    module: string = 'unknown',
    routePath: string = 'unknown',
    apiPath: string = '',
    additionalInfo?: {
      userType?: string
      userId?: string
      userCategory?: 'internal' | 'external' // Loop Kitchen staff vs restaurant staff/customers
      duration?: number
      version?: string
      email?: string
      org?: string
      orgId?: string
      orgName?: string
      accessLevel?: string
      methodName?: string
      severity?: 'low' | 'medium' | 'high' | 'critical'
      errorMessage?: string
      stack?: string
    }
  ) {
    try {
      // Time-based labels for range queries
      const timeLabels = {
        date: new Date().toISOString().split('T')[0],
        hour: new Date().getHours().toString()
      }

      // Base error context for all error metrics
      const baseLabels = {
        error_type: errorType,
        error_code: errorCode.toString(),
        module,
        route_path: routePath,
        severity: additionalInfo?.severity || 'medium',
        // Organization context for 2.1 Error by Org
        org_id: additionalInfo?.orgId || additionalInfo?.org || 'unknown',
        org_name: additionalInfo?.orgName || 'unknown',
        // User context for 2.3 Errors by User
        user_id: additionalInfo?.userId || 'anonymous',
        user_type: additionalInfo?.userType || 'unknown',
        user_category: additionalInfo?.userCategory || 'external', // Default to external
        user_access_level: additionalInfo?.accessLevel || 'unknown',
        ...timeLabels
      }

      // 1. Main error counter - queryable by time range, org, module, user
      await this.collectMetric('error', 'application_total', 1, baseLabels, 'counter' as const)

      // 2.1 Error tracking by Organization
      await this.collectMetric(
        'error',
        'by_org',
        1,
        {
          org_id: baseLabels.org_id,
          org_name: baseLabels.org_name,
          error_type: errorType,
          module,
          severity: baseLabels.severity,
          ...timeLabels
        },
        'counter' as const
      )

      // 2.2 Error tracking by Module
      await this.collectMetric(
        'error',
        'by_module',
        1,
        {
          module,
          error_type: errorType,
          severity: baseLabels.severity,
          org_id: baseLabels.org_id, // Include org for filtering
          ...timeLabels
        },
        'counter' as const
      )

      // 2.3 Error tracking by User (only if user is identified)
      if (additionalInfo?.userId && additionalInfo.userId !== 'anonymous') {
        await this.collectMetric(
          'error',
          'by_user',
          1,
          {
            user_id: additionalInfo.userId,
            user_type: baseLabels.user_type,
            user_category: baseLabels.user_category,
            error_type: errorType,
            module,
            org_id: baseLabels.org_id,
            ...timeLabels
          },
          'counter' as const
        )
      }
    } catch (error) {
      // Silently fail - metrics should never break the app
      console.warn('Failed to track error:', error)
    }
  }

  public async trackApiError(method: string, apiPath: string, statusCode: number, errorType: string, module: string, duration?: number, routePath?: string) {
    // Check if API error tracking is enabled
    if (!MetricsService.config.enabled || !MetricsService.config.enableApiErrorTracking) {
      return
    }

    // Use duration as the value if provided, otherwise use 1 for count
    const value = duration !== undefined ? duration : 1

    await this.collectMetric('error', 'api', value, {
      method,
      api_path: apiPath,
      status_code: statusCode.toString(),
      error_type: errorType,
      module,
      route_path: routePath || window.location.pathname
    })

    // Also track to the api_errors metric with by_route label
    // Tags: route, status_code
    await this.collectMetric(
      'api_errors',
      'by_route',
      1,
      {
        route: apiPath,
        status_code: statusCode.toString()
      },
      'counter' as const
    )

    // Also track to the api_errors metric with by_module label
    // Tags: module, status_code
    await this.collectMetric(
      'api_errors',
      'by_module',
      1,
      {
        module,
        status_code: statusCode.toString()
      },
      'counter' as const
    )
  }

  // Health Status
  public async trackHealthStatus(isHealthy: boolean) {
    await this.collectMetric('status', 'health', isHealthy ? 1 : 0)
  }

  // User Behavior with enhanced context support
  public async trackScrollDepth(depth: number, user?: { id?: string; type?: string; email?: string }) {
    await this.collectDebounced('user_behavior', 'scroll_depth', depth, undefined, {
      user,
      page: {
        url: window.location.href,
        title: document.title
      },
      type: 'gauge'
    })
  }
  // Custom metrics - now uses categorized format
  public async pushAPICallsCount(metric: MetricData) {
    // Convert to categorized metric
    await this.collectMetric('api', 'calls_count', metric.value, metric.labels, metric.type as 'counter' | 'gauge' | 'histogram')
  }

  // Legacy methods for backward compatibility
  public async trackUserLoginWithMagicLink() {
    await this.trackUserLogin('magic_link')
  }

  public async trackUserLoginWithPassword() {
    await this.trackUserLogin('password')
  }

  public async trackUserLoginWithSessionVerification() {
    await this.trackUserLogin('session_verification')
  }

  public async trackActiveUsers(count: number) {
    await this.collectMetric('user', 'active', count)
  }

  // Enhanced methods to leverage fe-metrics controller's full capabilities

  /**
   * Track user behavior with full context support
   */
  public async trackUserBehavior(
    action: string,
    value?: number,
    context?: {
      user?: { id?: string; type?: string; email?: string }
      session?: { id?: string; duration?: number }
      page?: { url?: string; title?: string; referrer?: string }
      metadata?: Record<string, any>
    }
  ) {
    await this.collectMetric('user', action, value, undefined, 'counter' as const)
  }

  /**
   * Track performance metrics with histogram support
   */
  public async trackPerformanceMetric(
    metric: string,
    duration: number,
    context?: {
      user?: { id?: string; type?: string; email?: string }
      page?: { url?: string; title?: string }
      metadata?: Record<string, any>
    }
  ) {
    await this.collectMetric('performance', metric, duration, undefined, 'histogram')
  }

  public async cleanup() {
    try {
      if (this.flushTimeoutId) {
        clearInterval(this.flushTimeoutId)
      }

      // Flush both metrics and Slack queues
      await Promise.all([this.flush(), this.flushSlackQueue()])
    } catch (error) {
      console.warn('Error during metrics cleanup:', error)
    }
  }
}

export const metricsService = MetricsService.getInstance()

// Export static methods for configuration
export const {
  getConfig: getMetricsConfig,
  updateConfig: updateMetricsConfig,
  isEnabled: isMetricsEnabled,
  enable: enableMetrics,
  disable: disableMetrics,
  enableSlackMetrics,
  disableSlackMetrics
} = MetricsService

// Export types for external use
export type { CategorizedMetricData, MetricData }

// Export enhanced methods for direct use
export const { trackUserBehavior, trackPerformanceMetric } = metricsService
